// "Proximité" — devices on the same network (PairDrop mode). Tap a device, choose files, the
// other side accepts or declines. No shared secret here, so the connection is not verified
// against a malicious signaling server; the screen says so.

import { Mesh } from '../core/mesh';
import { randomHex } from '../core/ids';
import { DropSender, type ReceiverStatus } from '../drop/sender';
import { DropReceiver } from '../drop/receiver';
import { formatBytes, parseCtl, type FileMeta } from '../drop/protocol';
import { cleanupOpfs } from '../drop/sinks';
import { h } from './dom';
import { deviceName, iceServers, randomDeviceName, setDeviceName, wsUrl } from './common';
import { incomingPanel } from './incoming';
import type { View } from './view';

const OUT_STATUS: Partial<Record<ReceiverStatus, string>> = {
  offered: 'en attente de réponse…',
  ready: 'accepté',
  receiving: 'envoi en cours',
  done: 'envoyé',
  declined: 'refusé',
  gone: 'parti',
};

type Offer = { peer: string; from: string; files: FileMeta[] };

export function nearbyView(root: HTMLElement): View {
  let mesh: Mesh | null = null;
  let error: string | null = null;
  let me = deviceName();
  const names = new Map<string, string>();
  const outgoing = new Map<string, DropSender>();
  const offers: Offer[] = [];
  const incoming: { receiver: DropReceiver; panel: ReturnType<typeof incomingPanel> }[] = [];
  let frame = 0;
  const schedule = () => {
    cancelAnimationFrame(frame);
    frame = requestAnimationFrame(render);
  };

  async function connect() {
    try {
      void cleanupOpfs('nb-');
      const m = new Mesh({ url: wsUrl('/ws/nearby'), iceServers: await iceServers() });
      mesh = m;
      m.addEventListener('authed', (e) => {
        m.send((e as CustomEvent).detail.peer, { t: 'hello', name: me });
        schedule();
      });
      m.addEventListener('gone', (e) => {
        const { peer } = (e as CustomEvent).detail;
        names.delete(peer);
        schedule();
      });
      m.addEventListener('down', schedule);
      m.addEventListener('ctl', (e) => {
        const { peer, msg } = (e as CustomEvent).detail;
        const parsed = parseCtl(msg);
        if (parsed?.t === 'hello') names.set(peer, parsed.name);
        if (parsed?.t === 'offer') offers.push({ peer, from: parsed.from, files: parsed.files });
        if (parsed) schedule();
      });
      await m.start();
    } catch (e) {
      error = (e as Error).message;
    }
    render();
  }

  function sendTo(peer: string, files: File[]) {
    if (!mesh || files.length === 0) return;
    outgoing.get(peer)?.dispose();
    const sender = new DropSender(mesh, files, { from: me, mode: 'nearby' });
    sender.addEventListener('change', schedule);
    sender.start();
    sender.offer(peer);
    outgoing.set(peer, sender);
    render();
  }

  function answer(offer: Offer, accept: boolean) {
    offers.splice(offers.indexOf(offer), 1);
    if (!mesh) return;
    mesh.send(offer.peer, { t: accept ? 'accept' : 'decline' });
    if (accept) {
      const receiver = new DropReceiver(mesh, offer.peer, offer.from, offer.files, `nb-${randomHex(4)}`);
      receiver.start();
      incoming.unshift({ receiver, panel: incomingPanel(receiver, { autoStart: true }) });
    }
    render();
  }

  function deviceRow(peer: string): HTMLElement {
    const name = names.get(peer) ?? 'appareil';
    const sender = outgoing.get(peer);
    const r = sender?.receivers.get(peer);
    const input = h('input', { type: 'file', multiple: true, hidden: true, onchange: () => sendTo(peer, [...(input.files ?? [])]) });
    const busy = r && (r.status === 'offered' || r.status === 'receiving' || r.status === 'ready');
    const progress = r && sender ? sender.bytesFor(r) / Math.max(1, sender.totalSize) : 0;
    return h(
      'li',
      { class: 'device' },
      input,
      h('div', { class: 'file-head' }, h('span', { class: 'file-name', text: name }), r && h('span', { class: r.status === 'declined' ? 'bad' : 'dim', text: OUT_STATUS[r.status] ?? '' })),
      r?.status === 'receiving' && h('div', { class: 'dim', text: `${Math.floor(progress * 100)} %` }),
      h('button', { class: 'btn small', disabled: !!busy, text: 'Envoyer des fichiers…', onclick: () => input.click() }),
    );
  }

  function renameButton() {
    return h('button', {
      class: 'btn small',
      text: 'changer',
      onclick: () => {
        me = randomDeviceName();
        setDeviceName(me);
        mesh?.authedPeers.forEach((p) => mesh!.send(p.id, { t: 'hello', name: me }));
        render();
      },
    });
  }

  function render() {
    const peers = mesh?.authedPeers.map((p) => p.id) ?? [];
    root.replaceChildren(
      ...[
        h('h1', { text: 'À proximité' }),
        h('p', { class: 'dim', text: 'Les appareils connectés au même réseau apparaissent ici : ouvre cette page sur l’autre appareil.' }),
        h('p', { class: 'row' }, h('span', { text: 'Toi : ' }), h('strong', { text: me }), renameButton()),
        error && h('p', { class: 'bad', text: `Connexion impossible : ${error}` }),
        ...offers.map((o) =>
          h(
            'section',
            { class: 'card offer' },
            h('p', { text: `${o.from} veut t’envoyer ${o.files.length} fichier${o.files.length > 1 ? 's' : ''} (${formatBytes(o.files.reduce((n, f) => n + f.size, 0))})` }),
            h('ul', { class: 'compact' }, ...o.files.slice(0, 5).map((f) => h('li', { class: 'dim', text: `${f.name} · ${formatBytes(f.size)}` })), o.files.length > 5 && h('li', { class: 'dim', text: `… et ${o.files.length - 5} autres` })),
            h('div', { class: 'row' }, h('button', { class: 'btn primary small', text: 'Accepter', onclick: () => answer(o, true) }), h('button', { class: 'btn small', text: 'Refuser', onclick: () => answer(o, false) })),
          ),
        ),
        ...incoming.map((i) => i.panel.el),
        h('h2', { text: 'Appareils' }),
        peers.length === 0 ? h('p', { class: 'dim', text: mesh ? 'Aucun appareil pour l’instant…' : 'Connexion…' }) : h('ul', { class: 'files' }, ...peers.map(deviceRow)),
        h('p', { class: 'dim small', text: 'Connexion non vérifiée : à utiliser sur un réseau de confiance. Pour un envoi vérifié, utilise un lien.' }),
      ].filter((n): n is HTMLElement => !!n),
    );
  }

  render();
  void connect();

  return {
    busy: () => [...outgoing.values()].some((s) => [...s.receivers.values()].some((r) => r.status === 'receiving')) || incoming.some((i) => !i.receiver.complete && !i.receiver.cancelled),
    destroy() {
      cancelAnimationFrame(frame);
      outgoing.forEach((s) => s.stop());
      incoming.forEach((i) => (i.panel.destroy(), i.receiver.stop()));
      mesh?.close();
    },
  };
}
